import dayjs from 'dayjs';

// Sample Wallets - Colors from design system
export const sampleWallets = {
  basic: [
    {
      id: 'sample_wallet_cash',
      name: 'Tiền mặt',
      type: 'basic',
      icon: 'wallet',
      color: '#5EBD82',  // green[500]
      basicWallet: {
        balance: 5000000,
      },
    },
    {
      id: 'sample_wallet_vietcombank',
      name: 'Thanh toán Vietcombank',
      type: 'basic',
      icon: 'bank',
      color: '#1FE0FF',  // teal[500]
      basicWallet: {
        balance: 3000000,
      },
    },
    {
      id: 'sample_wallet_techcombank',
      name: 'Tiết kiệm Techcombank',
      type: 'basic',
      icon: 'piggy',
      color: '#6C3DD3',  // purple[500]
      basicWallet: {
        balance: 6000000,
      },
    },
  ],
  credit: [
    {
      id: 'sample_wallet_credit_vcb',
      name: 'Thẻ tín dụng',
      type: 'credit',
      icon: 'credit-card',
      color: '#FF8247',  // orange[500]
      creditWallet: {
        balance: -54000000,
        creditLimit: 100000000,
        statementDateInMonth: 15,
        paymentDueDateInMonth: 25,
      },
    },
  ],
  loan: [
    {
      id: 'sample_wallet_loan',
      name: 'Vay nợ',
      type: 'loan',
      icon: 'hand-coins',
      color: '#F476A7',  // pink[500]
      loanWallet: {
        balance: 10200000,
      },
    },
  ],
};

// Sample Categories - Colors from design system
export const sampleCategories = {
  expense: [
    { id: 'cat_food', name: 'Ăn uống', group: 'Chi tiêu hàng ngày', icon: 'food', color: '#1FE0FF' },        // teal[500]
    { id: 'cat_restaurant', name: 'Ăn nhà hàng', group: 'Chi tiêu hàng ngày', icon: 'restaurant', color: '#FF8247' }, // orange[500]
    { id: 'cat_groceries', name: 'Thực phẩm', group: 'Chi tiêu hàng ngày', icon: 'shopping-cart', color: '#5EBD82' }, // green[500]
    { id: 'cat_snacks', name: 'Ăn vặt', group: 'Chi tiêu hàng ngày', icon: 'cookie', color: '#F476A7' },     // pink[500]
    { id: 'cat_coffee', name: 'Cà phê', group: 'Chi tiêu hàng ngày', icon: 'coffee', color: '#3B7651' },     // green[700]
    { id: 'cat_shopping', name: 'Mua sắm', group: 'Mua sắm', icon: 'shopping-bag', color: '#6C3DD3' },       // purple[500]
    { id: 'cat_transport', name: 'Di chuyển', group: 'Di chuyển', icon: 'car', color: '#F9CE46' },           // yellow[500]
    { id: 'cat_utilities', name: 'Điện nước', group: 'Hóa đơn', icon: 'electric', color: '#19B3CC' },        // teal[600]
    { id: 'cat_entertainment', name: 'Giải trí', group: 'Giải trí', icon: 'game', color: '#8964DC' },        // purple[400]
    { id: 'cat_health', name: 'Sức khỏe', group: 'Sức khỏe', icon: 'health', color: '#F8ADCA' },             // pink[300]
  ],
  income: [
    { id: 'cat_salary', name: 'Lương', group: 'Thu nhập', icon: 'money', color: '#1FE0FF' },                 // teal[500]
    { id: 'cat_bonus', name: 'Thưởng', group: 'Thu nhập', icon: 'gift', color: '#F476A7' },                  // pink[500]
    { id: 'cat_investment', name: 'Đầu tư', group: 'Thu nhập', icon: 'chart', color: '#FF8247' },            // orange[500]
    { id: 'cat_other_income', name: 'Thu nhập khác', group: 'Thu nhập', icon: 'coin', color: '#6C3DD3' },    // purple[500]
  ],
};

// Generate sample transactions for the past 3 months
export const generateSampleTransactions = () => {
  const transactions: any[] = [];
  const today = dayjs();

  // Expense transactions patterns
  const expensePatterns = [
    { category: 'cat_food', name: 'Ăn uống', minAmount: 30000, maxAmount: 150000, frequency: 'daily' },
    { category: 'cat_restaurant', name: 'Ăn nhà hàng', minAmount: 200000, maxAmount: 500000, frequency: 'weekly' },
    { category: 'cat_groceries', name: 'Thực phẩm', minAmount: 500000, maxAmount: 1500000, frequency: 'weekly' },
    { category: 'cat_snacks', name: 'Ăn vặt', minAmount: 20000, maxAmount: 100000, frequency: 'daily' },
    { category: 'cat_coffee', name: 'Cà phê', minAmount: 25000, maxAmount: 80000, frequency: 'daily' },
    { category: 'cat_shopping', name: 'Mua sắm', minAmount: 200000, maxAmount: 2000000, frequency: 'monthly' },
    { category: 'cat_transport', name: 'Di chuyển', minAmount: 50000, maxAmount: 300000, frequency: 'weekly' },
    { category: 'cat_utilities', name: 'Điện nước', minAmount: 500000, maxAmount: 1500000, frequency: 'monthly' },
    { category: 'cat_entertainment', name: 'Giải trí', minAmount: 100000, maxAmount: 500000, frequency: 'weekly' },
    { category: 'cat_health', name: 'Sức khỏe', minAmount: 100000, maxAmount: 1000000, frequency: 'monthly' },
  ];

  // Generate 3 months of data
  for (let monthOffset = 0; monthOffset < 3; monthOffset++) {
    const monthStart = today.subtract(monthOffset, 'month').startOf('month');
    const monthEnd = monthOffset === 0 ? today : today.subtract(monthOffset, 'month').endOf('month');
    const daysInRange = monthEnd.diff(monthStart, 'day') + 1;

    // Add salary at the beginning of each month
    transactions.push({
      id: `sample_tx_salary_${monthOffset}`,
      type: 'income',
      amount: 15000000,
      description: 'Lương tháng',
      categoryId: 'cat_salary',
      category: { id: 'cat_salary', name: 'Thu nhập•Lương' },
      walletId: 'sample_wallet_vietcombank',
      wallet: 'Thanh toán Vietcombank',
      transactionDate: monthStart.add(5, 'day').toISOString(),
    });

    // Add bonus occasionally (every other month)
    if (monthOffset % 2 === 0) {
      transactions.push({
        id: `sample_tx_bonus_${monthOffset}`,
        type: 'income',
        amount: 2000000,
        description: 'Thưởng dự án',
        categoryId: 'cat_bonus',
        category: { id: 'cat_bonus', name: 'Thu nhập•Thưởng' },
        walletId: 'sample_wallet_vietcombank',
        wallet: 'Thanh toán Vietcombank',
        transactionDate: monthStart.add(10, 'day').toISOString(),
      });
    }

    // Generate expense transactions
    expensePatterns.forEach((pattern, patternIndex) => {
      let transactionsCount = 0;

      switch (pattern.frequency) {
        case 'daily':
          transactionsCount = Math.floor(daysInRange * 0.7); // 70% of days
          break;
        case 'weekly':
          transactionsCount = Math.floor(daysInRange / 7);
          break;
        case 'monthly':
          transactionsCount = 1;
          break;
      }

      for (let i = 0; i < transactionsCount; i++) {
        const randomDay = Math.floor(Math.random() * daysInRange);
        const transactionDate = monthStart.add(randomDay, 'day');

        // Skip future dates
        if (transactionDate.isAfter(today)) continue;

        const amount = Math.floor(
          Math.random() * (pattern.maxAmount - pattern.minAmount) + pattern.minAmount
        );

        // Round to nearest 1000
        const roundedAmount = Math.round(amount / 1000) * 1000;

        const wallets = ['sample_wallet_cash', 'sample_wallet_vietcombank'];
        const randomWallet = wallets[Math.floor(Math.random() * wallets.length)];

        const category = sampleCategories.expense.find(c => c.id === pattern.category);

        transactions.push({
          id: `sample_tx_${monthOffset}_${patternIndex}_${i}`,
          type: 'expense',
          amount: -roundedAmount,
          description: pattern.name,
          categoryId: pattern.category,
          category: { id: pattern.category, name: `${category?.group}•${pattern.name}` },
          walletId: randomWallet,
          wallet: randomWallet === 'sample_wallet_cash' ? 'Tiền mặt' : 'Thanh toán Vietcombank',
          transactionDate: transactionDate.toISOString(),
        });
      }
    });
  }

  // Sort by date descending
  transactions.sort((a, b) =>
    dayjs(b.transactionDate).valueOf() - dayjs(a.transactionDate).valueOf()
  );

  return transactions;
};

// Calculate summary data from transactions
export const calculateSampleSummary = (transactions: any[]) => {
  const now = dayjs();
  const currentMonthStart = now.startOf('month');
  const currentMonthEnd = now.endOf('month');

  const currentMonthTransactions = transactions.filter(tx => {
    const txDate = dayjs(tx.transactionDate);
    return txDate.isAfter(currentMonthStart) && txDate.isBefore(currentMonthEnd);
  });

  const totalExpense = currentMonthTransactions
    .filter(tx => tx.type === 'expense')
    .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);

  const totalIncome = currentMonthTransactions
    .filter(tx => tx.type === 'income')
    .reduce((sum, tx) => sum + tx.amount, 0);

  const balance = totalIncome - totalExpense;

  // Calculate total assets
  const basicWalletsTotal = sampleWallets.basic.reduce(
    (sum, w) => sum + (w.basicWallet?.balance || 0), 0
  );
  const creditWalletsTotal = sampleWallets.credit.reduce(
    (sum, w) => sum + (w.creditWallet?.balance || 0), 0
  );
  const loanWalletsTotal = sampleWallets.loan.reduce(
    (sum, w) => sum + (w.loanWallet?.balance || 0), 0
  );

  const totalAssets = basicWalletsTotal + creditWalletsTotal + loanWalletsTotal;

  // Calculate category distribution
  const categoryDistribution: Record<string, number> = {};
  currentMonthTransactions
    .filter(tx => tx.type === 'expense')
    .forEach(tx => {
      const categoryName = tx.description || 'Khác';
      categoryDistribution[categoryName] = (categoryDistribution[categoryName] || 0) + Math.abs(tx.amount);
    });

  return {
    totalExpense,
    totalIncome,
    balance,
    totalAssets,
    categoryDistribution,
    transactionCount: currentMonthTransactions.length,
  };
};

// Sample asset fluctuation data (for chart)
// Very different random patterns for each wallet type
export const generateAssetFluctuationData = (walletType?: string) => {
  switch (walletType) {
    case 'BASIC':
      return [
        { month: '2025-06-01T00:00:00.000Z', monthLabel: '06/25', amount: 42500000 },
        { month: '2025-07-01T00:00:00.000Z', monthLabel: '07/25', amount: 78200000 },
        { month: '2025-08-01T00:00:00.000Z', monthLabel: '08/25', amount: 35800000 },
        { month: '2025-09-01T00:00:00.000Z', monthLabel: '09/25', amount: 92100000 },
        { month: '2025-10-01T00:00:00.000Z', monthLabel: '10/25', amount: 51600000 },
        { month: '2025-11-01T00:00:00.000Z', monthLabel: '11/25', amount: 67300000 },
      ];
    case 'CREDIT':
      return [
        { month: '2025-06-01T00:00:00.000Z', monthLabel: '06/25', amount: -65200000 },
        { month: '2025-07-01T00:00:00.000Z', monthLabel: '07/25', amount: -22800000 },
        { month: '2025-08-01T00:00:00.000Z', monthLabel: '08/25', amount: -48500000 },
        { month: '2025-09-01T00:00:00.000Z', monthLabel: '09/25', amount: -8900000 },
        { month: '2025-10-01T00:00:00.000Z', monthLabel: '10/25', amount: -71300000 },
        { month: '2025-11-01T00:00:00.000Z', monthLabel: '11/25', amount: -35600000 },
      ];
    case 'LOAN':
      return [
        { month: '2025-06-01T00:00:00.000Z', monthLabel: '06/25', amount: 18700000 },
        { month: '2025-07-01T00:00:00.000Z', monthLabel: '07/25', amount: -12400000 },
        { month: '2025-08-01T00:00:00.000Z', monthLabel: '08/25', amount: 45200000 },
        { month: '2025-09-01T00:00:00.000Z', monthLabel: '09/25', amount: 8600000 },
        { month: '2025-10-01T00:00:00.000Z', monthLabel: '10/25', amount: -28900000 },
        { month: '2025-11-01T00:00:00.000Z', monthLabel: '11/25', amount: 32100000 },
      ];
    default: // ALL
      return [
        { month: '2025-06-01T00:00:00.000Z', monthLabel: '06/25', amount: -4000000 },
        { month: '2025-07-01T00:00:00.000Z', monthLabel: '07/25', amount: 43000000 },
        { month: '2025-08-01T00:00:00.000Z', monthLabel: '08/25', amount: 32500000 },
        { month: '2025-09-01T00:00:00.000Z', monthLabel: '09/25', amount: 91800000 },
        { month: '2025-10-01T00:00:00.000Z', monthLabel: '10/25', amount: -48600000 },
        { month: '2025-11-01T00:00:00.000Z', monthLabel: '11/25', amount: 63800000 },
      ];
  }
};

// Sample statistics data for onboarding (matches StatisticRecord interface)
export const getSampleStatistics = () => {
  return [
    {
      type: 'total-expense',
      value: 4538000,
      previous: 3200000,
      net: 1338000,
      isIncrease: true,
    },
    {
      type: 'total-income',
      value: 17000000,
      previous: 15000000,
      net: 2000000,
      isIncrease: true,
    },
    {
      type: 'net-flow',
      value: 12462000,
      previous: 11800000,
      net: 662000,
      isIncrease: true,
    },
    {
      type: 'money-out',
      value: 5200000,
      previous: 4800000,
      net: 400000,
      isIncrease: true,
    },
    {
      type: 'money-in',
      value: 17500000,
      previous: 15500000,
      net: 2000000,
      isIncrease: true,
    },
    {
      type: 'net-cash-flow',
      value: 12300000,
      previous: 10700000,
      net: 1600000,
      isIncrease: true,
    },
  ];
};

// SVG icons for sample data categories - matching real data style
const SAMPLE_ICONS = {
  // Food/dining - bowl with steam
  food: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.1 13.34l2.83-2.83L3.91 3.5a4.008 4.008 0 000 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z" fill="currentColor"/></svg>',
  // Shopping cart
  shopping: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1.003 1.003 0 0020 4H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" fill="currentColor"/></svg>',
  // Heart - health/wellness
  heart: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="currentColor"/></svg>',
  // Coffee cup
  coffee: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 3H4v10c0 2.21 1.79 4 4 4h6c2.21 0 4-1.79 4-4v-3h2c1.11 0 2-.89 2-2V5c0-1.11-.89-2-2-2zm0 5h-2V5h2v3zM2 21h18v-2H2v2z" fill="currentColor"/></svg>',
  // Bus/transport
  transport: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z" fill="currentColor"/></svg>',
  // Lightning bolt - utilities
  utilities: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 2v11h3v9l7-12h-4l4-8z" fill="currentColor"/></svg>',
  // Game controller - entertainment
  entertainment: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5S18.67 9 19.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="currentColor"/></svg>',
  // People/group
  people: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/></svg>',
  // House/home
  home: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="currentColor"/></svg>',
  // Money/wallet
  money: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="currentColor"/></svg>',
  // Cat/pets
  pet: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="4.5" cy="9.5" r="2.5" fill="currentColor"/><circle cx="9" cy="5.5" r="2.5" fill="currentColor"/><circle cx="15" cy="5.5" r="2.5" fill="currentColor"/><circle cx="19.5" cy="9.5" r="2.5" fill="currentColor"/><path d="M17.34 14.86c-.87-1.02-1.6-1.89-2.48-2.91-.46-.54-1.05-1.08-1.75-1.32-.11-.04-.22-.07-.33-.09-.25-.04-.52-.04-.78-.04s-.53 0-.79.05c-.11.02-.22.05-.33.09-.7.24-1.28.78-1.75 1.32-.87 1.02-1.6 1.89-2.48 2.91-1.31 1.31-2.92 2.76-2.62 4.79.29 1.02 1.02 2.03 2.33 2.32.73.15 3.06-.44 5.54-.44h.18c2.48 0 4.81.58 5.54.44 1.31-.29 2.04-1.31 2.33-2.32.31-2.04-1.3-3.49-2.61-4.8z" fill="currentColor"/></svg>',
  // Building/chart - other
  other: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" fill="currentColor"/></svg>',
  // Fork and spoon - restaurant
  restaurant: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z" fill="currentColor"/></svg>',
  // Gift/bonus
  gift: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z" fill="currentColor"/></svg>',
  // Salary/income
  salary: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" fill="currentColor"/></svg>',
};

// Sample expense allocation data for pie chart
// Colors and icons match real data style
export const getSampleExpenseAllocation = () => {
  return [
    { name: 'Thú cưng', value: 900000, percentage: 20, color: '#6C3DD3', categoryId: 'cat_pet', icon: SAMPLE_ICONS.pet },              // purple - cat icon
    { name: 'Ăn uống', value: 810000, percentage: 18, color: '#1FE0FF', categoryId: 'cat_food', icon: SAMPLE_ICONS.restaurant },       // teal - fork/spoon
    { name: 'Mua sắm', value: 720000, percentage: 16, color: '#19B3CC', categoryId: 'cat_shopping', icon: SAMPLE_ICONS.shopping },     // teal[600] - cart
    { name: 'Sức khỏe', value: 540000, percentage: 12, color: '#8964DC', categoryId: 'cat_health', icon: SAMPLE_ICONS.heart },         // purple[400] - heart
    { name: 'Cà phê', value: 225000, percentage: 5, color: '#1FE0FF', categoryId: 'cat_coffee', icon: SAMPLE_ICONS.coffee },           // teal - coffee cup
    { name: 'Bạn bè', value: 180000, percentage: 4, color: '#F9CE46', categoryId: 'cat_friends', icon: SAMPLE_ICONS.people },          // yellow - people
    { name: 'Giải trí', value: 180000, percentage: 4, color: '#F45B69', categoryId: 'cat_entertainment', icon: SAMPLE_ICONS.entertainment }, // red - game
    { name: 'Gia đình', value: 135000, percentage: 3, color: '#5EBD82', categoryId: 'cat_family', icon: SAMPLE_ICONS.people },         // green - people
    { name: 'Di chuyển', value: 135000, percentage: 3, color: '#5EBD82', categoryId: 'cat_transport', icon: SAMPLE_ICONS.transport },  // green - bus
    { name: 'Điện nước', value: 90000, percentage: 2, color: '#1FE0FF', categoryId: 'cat_utilities', icon: SAMPLE_ICONS.utilities },   // teal - lightning
    { name: 'Khác', value: 315000, percentage: 7, color: '#757F9F', categoryId: 'cat_other', icon: SAMPLE_ICONS.other },               // grey - chart
  ];
};

// Sample expense allocation data grouped by category group
// Icons and colors match real data style (from Gom nhóm ON image)
export const getSampleExpenseAllocationGrouped = () => {
  return [
    {
      name: 'Chi tiêu hàng ngày',
      value: 1800000,
      percentage: 40,
      color: '#1FE0FF',           // teal - food bowl
      categoryId: 'group_daily',
      icon: SAMPLE_ICONS.food,
      items: [
        { name: 'Ăn uống', value: 900000, color: '#1FE0FF', categoryId: 'cat_food', icon: SAMPLE_ICONS.restaurant },
        { name: 'Cà phê', value: 450000, color: '#3B7651', categoryId: 'cat_coffee', icon: SAMPLE_ICONS.coffee },
        { name: 'Ăn vặt', value: 450000, color: '#F476A7', categoryId: 'cat_snacks', icon: SAMPLE_ICONS.food },
      ]
    },
    {
      name: 'Nhà cửa',
      value: 1620000,
      percentage: 36,
      color: '#6C3DD3',           // purple - house
      categoryId: 'group_housing',
      icon: SAMPLE_ICONS.home,
      items: [
        { name: 'Tiền nhà', value: 1000000, color: '#6C3DD3', categoryId: 'cat_rent', icon: SAMPLE_ICONS.home },
        { name: 'Điện nước', value: 620000, color: '#19B3CC', categoryId: 'cat_utilities', icon: SAMPLE_ICONS.utilities },
      ]
    },
    {
      name: 'Tài chính',
      value: 315000,
      percentage: 7,
      color: '#757F9F',           // grey - money
      categoryId: 'group_finance',
      icon: SAMPLE_ICONS.money,
      items: [
        { name: 'Tiết kiệm', value: 315000, color: '#757F9F', categoryId: 'cat_savings', icon: SAMPLE_ICONS.money },
      ]
    },
    {
      name: 'Gia đình',
      value: 270000,
      percentage: 6,
      color: '#F476A7',           // pink - house
      categoryId: 'group_family',
      icon: SAMPLE_ICONS.home,
      items: [
        { name: 'Gia đình', value: 270000, color: '#F476A7', categoryId: 'cat_family', icon: SAMPLE_ICONS.people },
      ]
    },
    {
      name: 'Phần thưởng',
      value: 225000,
      percentage: 5,
      color: '#F9CE46',           // yellow - trophy
      categoryId: 'group_rewards',
      icon: SAMPLE_ICONS.gift,
      items: [
        { name: 'Quà tặng', value: 225000, color: '#F9CE46', categoryId: 'cat_gifts', icon: SAMPLE_ICONS.gift },
      ]
    },
    {
      name: 'Mua sắm',
      value: 180000,
      percentage: 4,
      color: '#5EBD82',           // green - shopping
      categoryId: 'group_shopping',
      icon: SAMPLE_ICONS.shopping,
      items: [
        { name: 'Mua sắm', value: 180000, color: '#5EBD82', categoryId: 'cat_shopping', icon: SAMPLE_ICONS.shopping },
      ]
    },
    {
      name: 'Giải trí',
      value: 180000,
      percentage: 4,
      color: '#F45B69',           // red - star/entertainment
      categoryId: 'group_entertainment',
      icon: SAMPLE_ICONS.entertainment,
      items: [
        { name: 'Giải trí', value: 180000, color: '#F45B69', categoryId: 'cat_entertainment', icon: SAMPLE_ICONS.entertainment },
      ]
    },
  ];
};

// Sample income allocation data
// Colors from design system
export const getSampleIncomeAllocation = () => {
  return [
    { name: 'Lương', value: 15000000, percentage: 88, color: '#1FE0FF', categoryId: 'cat_salary', icon: SAMPLE_ICONS.salary },
    { name: 'Thưởng', value: 2000000, percentage: 12, color: '#F476A7', categoryId: 'cat_bonus', icon: SAMPLE_ICONS.gift },
  ];
};

// Sample trending data for line/bar charts
export const getSampleTrendingData = () => {
  const today = dayjs();
  const data = [];

  for (let i = 6; i >= 0; i--) {
    const date = today.subtract(i, 'month');
    data.push({
      month: date.format('MM/YYYY'),
      label: date.format('T.MM'),
      expense: Math.floor(3500000 + Math.random() * 2000000),
      income: Math.floor(14000000 + Math.random() * 4000000),
    });
  }

  return data;
};

// Sample wallets with proper balance for display - Colors from design system
export const getSampleWalletsForDisplay = () => {
  return [
    {
      id: 'sample_wallet_cash',
      name: 'Tiền mặt',
      type: 'basic',
      icon: 'wallet',
      color: '#5EBD82',  // green[500]
      balance: 5000000,
      currency: 'VND',
    },
    {
      id: 'sample_wallet_vietcombank',
      name: 'Vietcombank',
      type: 'basic',
      icon: 'bank',
      color: '#1FE0FF',  // teal[500]
      balance: 25000000,
      currency: 'VND',
    },
    {
      id: 'sample_wallet_techcombank',
      name: 'Tiết kiệm',
      type: 'basic',
      icon: 'piggy',
      color: '#6C3DD3',  // purple[500]
      balance: 50000000,
      currency: 'VND',
    },
  ];
};

// Sample total asset balance
export const getSampleTotalBalance = () => {
  return {
    total: 57000000,
    change: 5200000,
    changePercent: 6.9,
    isIncrease: true,
  };
};

// Sample credit card data for onboarding
export const getSampleCreditCard = () => {
  return {
    id: 'sample_wallet_credit_techcombank',
    name: 'Thẻ tín dụng Techcombank',
    type: 'credit',
    icon: 'credit-card',
    color: '#FF8247',
    creditWallet: {
      balance: -5500000,
      creditLimit: 100000000,
      statementDateInMonth: 14,
      paymentDueDateInMonth: 25,
    },
  };
};

// Sample credit card statements for onboarding
// Based on image: 3,000,000 (14/01-13/02, current), 2,000,000 (14/12-13/01), 1,500,000 (14/11-13/12)
export const getSampleCreditStatements = () => {
  return [
    {
      id: 1,
      startDate: '2025-01-14T00:00:00.000Z',
      endDate: '2025-02-13T23:59:59.999Z',
      dueDate: '2025-02-25T23:59:59.999Z',
      previousBalance: -2500000,
      newCharges: -500000,
      endBalance: -3000000,
      isCurrentStatementPeriod: true,
    },
    {
      id: 2,
      startDate: '2024-12-14T00:00:00.000Z',
      endDate: '2025-01-13T23:59:59.999Z',
      dueDate: '2025-01-25T23:59:59.999Z',
      previousBalance: -1500000,
      newCharges: -500000,
      endBalance: -2000000,
      isCurrentStatementPeriod: false,
    },
    {
      id: 3,
      startDate: '2024-11-14T00:00:00.000Z',
      endDate: '2024-12-13T23:59:59.999Z',
      dueDate: '2024-12-25T23:59:59.999Z',
      previousBalance: -1000000,
      newCharges: -500000,
      endBalance: -1500000,
      isCurrentStatementPeriod: false,
    },
  ];
};

// Sample loan wallet data for onboarding
// Based on image: "0 cần trả" (payable=0), "2,000,000 cần thu" (receivable=2M)
// Sub-tabs: "Vay (500K)", "Trả nợ (500K)"
export const getSampleLoanWallet = () => {
  return {
    id: 'sample_wallet_loan',
    name: 'Em Diệp',
    type: 'loan',
    icon: 'hand-coins',
    color: '#F476A7',
    loanWallet: {
      balance: 1900000,
    },
    walletTransactionCounts: {
      borrowing_sum: 500000,    // Vay (500K)
      repayment_sum: 500000,    // Trả nợ (500K) - so payable = 500K - 500K = 0
      lending_sum: 2000000,     // Total lent
      collected_sum: 0,         // Collected = 0, so receivable = 2,000,000
    },
  };
};

// Sample loan transactions for onboarding
export const getSampleLoanTransactions = () => {
  const today = dayjs();
  return {
    borrowing: [
      {
        id: 'sample_loan_tx_1',
        type: 'borrowing',
        amount: 200000,
        description: 'Vay học phí',
        counterparty: 'Thanh toán Vietcombank',
        transactionDate: today.subtract(5, 'day').toISOString(),
        createdAt: today.subtract(5, 'day').toISOString(),
      },
      {
        id: 'sample_loan_tx_2',
        type: 'borrowing',
        amount: 50000,
        description: 'Vay chi tiêu',
        note: 'Mua trà sữa',
        counterparty: null,
        transactionDate: today.subtract(3, 'day').toISOString(),
        createdAt: today.subtract(3, 'day').toISOString(),
      },
    ],
    lending: [
      {
        id: 'sample_loan_tx_3',
        type: 'lending',
        amount: 500000,
        description: 'Cho vay',
        counterparty: 'Tiền mặt',
        transactionDate: today.subtract(10, 'day').toISOString(),
        createdAt: today.subtract(10, 'day').toISOString(),
      },
      {
        id: 'sample_loan_tx_4',
        type: 'lending',
        amount: 1500000,
        description: 'Cho vay tiền học',
        counterparty: 'Thanh toán Vietcombank',
        transactionDate: today.subtract(15, 'day').toISOString(),
        createdAt: today.subtract(15, 'day').toISOString(),
      },
    ],
    repayment: [],
    collected: [
      {
        id: 'sample_loan_tx_5',
        type: 'collected',
        amount: 500000,
        description: 'Thu nợ',
        counterparty: 'Tiền mặt',
        transactionDate: today.subtract(2, 'day').toISOString(),
        createdAt: today.subtract(2, 'day').toISOString(),
      },
    ],
  };
};

// Sample loans data for useLoans hook (matching Loan interface)
// Based on image: Vay học phí (200K), Vay chi tiêu - Mua trà sữa (50K)
export const getSampleLoans = (loanStatus: string) => {
  const borrowingLoans = [
    {
      id: 'sample_loan_1',
      publicId: 'sample_loan_1',
      amount: 200000,
      loanDate: '2025-09-02T10:00:00.000Z',
      startDate: '2025-09-02T10:00:00.000Z',
      endDate: '2025-10-02T10:00:00.000Z',
      status: 'active' as const,
      loanType: 'borrowing' as const,
      description: 'Vay học phí',
      repaidAmount: 200000,
      remainingAmount: 0,
      counterparty: { name: 'Thanh toán Vietcombank' },
    },
    {
      id: 'sample_loan_2',
      publicId: 'sample_loan_2',
      amount: 50000,
      loanDate: '2025-09-02T14:00:00.000Z',
      startDate: '2025-09-02T14:00:00.000Z',
      endDate: '2025-10-02T14:00:00.000Z',
      status: 'active' as const,
      loanType: 'borrowing' as const,
      description: 'Vay chi tiêu',
      note: 'Mua trà sữa',
      repaidAmount: 50000,
      remainingAmount: 0,
      counterparty: null,
    },
    {
      id: 'sample_loan_6',
      publicId: 'sample_loan_6',
      amount: 250000,
      loanDate: '2025-08-15T10:00:00.000Z',
      startDate: '2025-08-15T10:00:00.000Z',
      endDate: '2025-09-15T10:00:00.000Z',
      status: 'active' as const,
      loanType: 'borrowing' as const,
      description: 'Vay tiền ăn',
      repaidAmount: 250000,
      remainingAmount: 0,
      counterparty: { name: 'Tiền mặt' },
    },
  ];

  const lendingLoans = [
    {
      id: 'sample_loan_3',
      publicId: 'sample_loan_3',
      amount: 500000,
      loanDate: '2025-08-20T10:00:00.000Z',
      startDate: '2025-08-20T10:00:00.000Z',
      endDate: '2025-10-20T10:00:00.000Z',
      status: 'active' as const,
      loanType: 'lending' as const,
      description: 'Cho vay',
      repaidAmount: 0,
      remainingAmount: 500000,
      counterparty: { name: 'Tiền mặt' },
    },
    {
      id: 'sample_loan_4',
      publicId: 'sample_loan_4',
      amount: 1500000,
      loanDate: '2025-08-10T10:00:00.000Z',
      startDate: '2025-08-10T10:00:00.000Z',
      endDate: '2025-11-10T10:00:00.000Z',
      status: 'active' as const,
      loanType: 'lending' as const,
      description: 'Cho vay tiền học',
      repaidAmount: 0,
      remainingAmount: 1500000,
      counterparty: { name: 'Thanh toán Vietcombank' },
    },
  ];

  // Repaying loans totaling 500K to match "Trả nợ (500K)"
  const repayingLoans = [
    {
      id: 'sample_loan_7',
      publicId: 'sample_loan_7',
      amount: 300000,
      loanDate: '2025-09-05T10:00:00.000Z',
      startDate: '2025-09-05T10:00:00.000Z',
      endDate: '2025-09-05T10:00:00.000Z',
      status: 'completed' as const,
      loanType: 'repaying' as const,
      description: 'Trả nợ',
      repaidAmount: 300000,
      remainingAmount: 0,
      counterparty: { name: 'Tiền mặt' },
    },
    {
      id: 'sample_loan_8',
      publicId: 'sample_loan_8',
      amount: 200000,
      loanDate: '2025-09-03T10:00:00.000Z',
      startDate: '2025-09-03T10:00:00.000Z',
      endDate: '2025-09-03T10:00:00.000Z',
      status: 'completed' as const,
      loanType: 'repaying' as const,
      description: 'Trả nợ học phí',
      repaidAmount: 200000,
      remainingAmount: 0,
      counterparty: { name: 'Thanh toán Vietcombank' },
    },
  ];

  const collectLoans: any[] = [];

  switch (loanStatus) {
    case 'borrowing':
      return borrowingLoans;
    case 'lending':
      return lendingLoans;
    case 'repaying':
      return repayingLoans;
    case 'collect':
      return collectLoans;
    default:
      return [];
  }
};

// Export all sample data as a bundle
export const getSampleData = () => {
  const transactions = generateSampleTransactions();
  const summary = calculateSampleSummary(transactions);
  const assetFluctuation = generateAssetFluctuationData();

  return {
    wallets: sampleWallets,
    categories: sampleCategories,
    transactions,
    summary,
    assetFluctuation,
    statistics: getSampleStatistics(),
    expenseAllocation: getSampleExpenseAllocation(),
    incomeAllocation: getSampleIncomeAllocation(),
    trending: getSampleTrendingData(),
    walletsDisplay: getSampleWalletsForDisplay(),
    totalBalance: getSampleTotalBalance(),
    creditCard: getSampleCreditCard(),
    creditStatements: getSampleCreditStatements(),
    loanWallet: getSampleLoanWallet(),
    loanTransactions: getSampleLoanTransactions(),
  };
};
